import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, errorInfo);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        className="min-h-screen flex items-center justify-center bg-background px-4"
      >
        <div className="max-w-md w-full text-center space-y-6">
          <div className="mx-auto w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-destructive" aria-hidden="true" />
          </div>
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-foreground">
              Algo deu errado
            </h1>
            <p className="text-base text-muted-foreground">
              Encontramos um erro inesperado. Tente recarregar a página ou voltar ao início.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              onClick={this.handleReload}
              className="h-12 gap-2"
              aria-label="Recarregar a página"
            >
              <RefreshCw className="w-4 h-4" aria-hidden="true" />
              Recarregar
            </Button>
            <Button
              onClick={this.handleGoHome}
              variant="outline"
              className="h-12 gap-2"
              aria-label="Voltar à página inicial"
            >
              <Home className="w-4 h-4" aria-hidden="true" />
              Início
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
